import React from "react";

const shortId = (value) => value ? `${value.slice(0, 8)}…${value.slice(-4)}` : "—";

export default function UserRevenuePanel({ api }) {
  const [users, setUsers] = React.useState([]);
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [identity, setIdentity] = React.useState(null);
  const [loadingIdentity, setLoadingIdentity] = React.useState("");
  const identityGeneration = React.useRef(0);
  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await api.getUsers();
      if (!Array.isArray(result.users)) throw new Error("Invalid user list.");
      setUsers(result.users);
    } catch (_) {
      setError("User snapshots are unavailable.");
    } finally {
      setLoading(false);
    }
  }, [api]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => () => { identityGeneration.current += 1; }, []);
  React.useEffect(() => {
    if (!identity) return undefined;
    const timeout = setTimeout(() => setIdentity(null), 60_000);
    return () => clearTimeout(timeout);
  }, [identity]);

  const loadIdentity = async (publicUserId) => {
    const requestGeneration = ++identityGeneration.current;
    setIdentity(null); setLoadingIdentity(publicUserId); setError("");
    try {
      const created = await api.createIdentityLookup(publicUserId);
      if (requestGeneration !== identityGeneration.current) return;
      if (created.status === "ACCEPTED") { setIdentity({ publicUserId, ...created.identity }); return; }
      for (let attempt = 0; attempt < 12; attempt += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 1000));
        if (requestGeneration !== identityGeneration.current) return;
        try { const result = await api.readIdentityLookup(created.lookupId); if (requestGeneration !== identityGeneration.current) return; if (result.status === "ACCEPTED") { setIdentity({ publicUserId, ...result.identity }); return; } }
        catch (cause) { if (cause?.status !== 202) throw cause; }
      }
      throw new Error("Identity lookup timed out.");
    } catch (error) { if (requestGeneration === identityGeneration.current) setError(error?.code === "IDENTITY_RESULT_EXPIRED" ? error.message : "Telegram identity is unavailable. App backend may be offline; retry when it is connected."); }
    finally { if (requestGeneration === identityGeneration.current) setLoadingIdentity(""); }
  };

  return <section className="adminDataPanel">
    {error && <div className="adminAlert adminAlertError">{error}</div>}
    {identity && <div className="adminIdentityCard"><strong>{identity.displayName}</strong><span>{identity.username ? `@${identity.username}` : "No username"}</span><span>Telegram ID {identity.telegramId}</span><button type="button" onClick={() => setIdentity(null)}>Hide identity</button></div>}
    <div className="adminTableWrap"><table className="adminTable"><thead><tr><th>User</th><th>Plan / state</th><th>Referral rule</th><th>Balances</th><th>Identity</th></tr></thead><tbody>
      {users.map((user) => <tr key={user.publicUserId}><td title={user.publicUserId}>{user.profile?.displayLabel || shortId(user.publicUserId)}</td><td>{user.profile?.plan || "Free"}<br/><small>{user.profile?.accountStatus || "Active"}</small></td><td>{user.referrals?.commissionRatePercent ?? 10}%<br/><small>{user.referrals?.commissionRateSource || "plan"}</small></td><td>{user.referrals?.balances?.available ?? 0} available<br/><small>{user.referrals?.balances?.reserved ?? 0} reserved</small></td><td><button type="button" className="adminSecondaryButton" disabled={loadingIdentity === user.publicUserId} onClick={() => loadIdentity(user.publicUserId)}>{loadingIdentity === user.publicUserId ? "Loading…" : "Load Telegram identity"}</button></td></tr>)}
    </tbody></table></div>
    {loading && <p role="status">Loading user snapshots…</p>}
    {!loading && !error && users.length === 0 && <p>No UUID user snapshots have been published yet.</p>}
  </section>;
}
